// AboutComponent.js
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import aboutImage from "../assets/caregivers-engage-image.jpg";

const AboutComponent = () => {
  return (
    <section className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
          About Us
        </p>
        <h2 className="font-display font-light text-bnInk leading-[1.1] text-3xl md:text-4xl lg:text-5xl mb-5">
          Warm, person-centred home and aged care
        </h2>
        <p className="text-bnBody text-base leading-relaxed max-w-2xl mb-12">
          Warm, person-centred home and aged care for older Australians and
          their families, delivered with care and respect across the Canberra
          region.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
          <motion.img
            src={aboutImage}
            alt="A carer supporting an older person at home"
            className="w-full h-[420px] lg:h-[520px] object-cover rounded-2xl"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          />

          <motion.div
            className="text-left space-y-4 text-bnBody text-base leading-relaxed"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p>
              B & N Enterprise is a home and aged care provider supporting older
              people to live well in the place they call home. We help with
              everyday personal care, domestic assistance, social support, and
              respite, and we tailor every plan around the goals, routines, and
              preferences of the person we are supporting.
            </p>

            <p>
              Our approach is built on dignity and choice. We take the time to
              understand each person and their family, then match a small,
              consistent team of carers who learn what matters most. Familiar
              faces and steady routines help the people we support feel
              comfortable, safe, and genuinely cared for.
            </p>

            <p>
              Families work with a dedicated coordinator who stays close to your
              care from start to finish: an unhurried first conversation, a
              personalised care plan, careful matching of carers, and regular
              check-ins to make sure the support continues to fit as needs
              change.
            </p>

            <p>
              Quality and safety guide everything we do. We listen to feedback,
              review care plans regularly, and respond quickly when
              circumstances change, so families have peace of mind that their
              loved one is in good hands.
            </p>

            <p>
              We take privacy seriously. Personal and health information is
              handled securely, shared only with the people directly involved in
              care, and kept no longer than needed to support you well.
            </p>
          </motion.div>
        </div>

        {/* Mini Highlights */}
        <motion.div
          className="bg-bnCream rounded-2xl p-8 md:p-10 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h3 className="font-display font-normal text-bnInk text-2xl leading-snug mb-8">
            Why Families Choose Us
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start gap-4">
              <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full border border-bnAccent text-bnAccent">
                <ArrowRight size={16} />
              </span>
              <div>
                <p className="text-bnInk font-medium text-lg">Person-centred</p>
                <p className="text-bnBody text-sm mt-1">
                  Care shaped around each person's goals, routines, and
                  preferences.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full border border-bnAccent text-bnAccent">
                <ArrowRight size={16} />
              </span>
              <div>
                <p className="text-bnInk font-medium text-lg">
                  Consistent carers
                </p>
                <p className="text-bnBody text-sm mt-1">
                  A small, familiar team so faces and routines stay reassuringly
                  steady.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full border border-bnAccent text-bnAccent">
                <ArrowRight size={16} />
              </span>
              <div>
                <p className="text-bnInk font-medium text-lg">
                  Dignity and respect
                </p>
                <p className="text-bnBody text-sm mt-1">
                  Support delivered with kindness, patience, and genuine warmth.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full border border-bnAccent text-bnAccent">
                <ArrowRight size={16} />
              </span>
              <div>
                <p className="text-bnInk font-medium text-lg">
                  Clear communication
                </p>
                <p className="text-bnBody text-sm mt-1">
                  A dedicated coordinator and regular check-ins keep families
                  informed.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full border border-bnAccent text-bnAccent">
                <ArrowRight size={16} />
              </span>
              <div>
                <p className="text-bnInk font-medium text-lg">
                  Care that adapts
                </p>
                <p className="text-bnBody text-sm mt-1">
                  Plans reviewed regularly so support keeps fitting as needs
                  change.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Offices & Hours */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="bg-white border border-bnBorder rounded-2xl p-8">
            <h4 className="font-display font-normal text-bnInk text-xl leading-snug mb-5 flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-bnAccent" />
              Get in Touch
            </h4>
            <div className="space-y-3 text-sm text-bnBody">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-2 text-bnAccent" />
                <span>23 Mahony Ct, Weston ACT 2611, Australia</span>
              </div>
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-2 text-bnAccent" />
                <span>+61 480 804 640</span>
              </div>
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-2 text-bnAccent" />
                <span>info@bnenterprise.care</span>
              </div>
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-2 text-bnAccent" />
                <span>support@bnenterprise.care</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-bnBorder rounded-2xl p-8">
            <h4 className="font-display font-normal text-bnInk text-xl leading-snug mb-5 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-bnAccent" />
              When We Care
            </h4>
            <div className="space-y-3 text-sm text-bnBody">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2 text-bnAccent" />
                <span>
                  Care available across the week, arranged to suit each person.
                </span>
              </div>
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-2 text-bnAccent" />
                <span>Call us to talk through your family's needs.</span>
              </div>
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-2 text-bnAccent" />
                <span>We respond to enquiries warmly and promptly.</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTAs */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <Link
            to="/about"
            className="inline-flex items-center gap-2 bg-bnAccent hover:bg-bnAccentDark text-white font-medium text-sm px-7 py-3.5 rounded-full transition-colors"
          >
            Learn More
            <ArrowRight size={16} />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 border border-bnBorder text-bnInk hover:border-bnAccent hover:text-bnAccent font-medium text-sm px-7 py-3.5 rounded-full transition-colors"
          >
            Contact Us
            <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutComponent;
